/**
 * Per-question-type handlers that transform raw server `data` (snake_case)
 * into camelCase objects with sensible defaults.
 *
 * Each handler receives the raw `data` sub-object from the serializer and
 * returns a fully-defaulted object ready for use in React state.
 */

function handleMultipleChoice(raw) {
  return {
    viewtype: raw.viewtype || null,
    maxanswers: raw.maxanswers || 1,
    options: (raw.options || []).map(opt => ({
      guid: opt.guid,
      text: opt.text,
      perOptionFeedback: opt.per_option_feedback || null,
    })),
    minSelections: raw.min_selections ?? 1,
    maxSelections: raw.max_selections ?? 1,
  }
}

function handleTrueFalse(raw) {
  return {
    minSelections: raw.min_selections ?? 1,
    maxSelections: raw.max_selections ?? 1,
  }
}

function handleShortAnswer(raw) {
  return {
    answerType: raw.answer_type || 'text',
    minSelections: raw.min_selections ?? 1,
    maxSelections: raw.max_selections ?? 1,
  }
}

function handleEssay(raw) {
  return {
    essaySize: raw.essay_size || 300,
    grading: raw.grading || '',
    minSelections: raw.min_selections ?? 1,
    maxSelections: raw.max_selections ?? 1,
  }
}

function handleCoder(raw) {
  return {
    coderSize: raw.coder_size || 300,
    language: raw.language || 'plain_text',
    grading: raw.grading || '',
    minSelections: raw.min_selections ?? 1,
    maxSelections: raw.max_selections ?? 1,
  }
}

function handleFileUpload(raw) {
  return {
    grading: raw.grading || '',
    minRequired: raw.min_required || 0,
    maxRequired: raw.max_required || 1,
    filenameMask: raw.filename_mask || '',
    filenameHint: raw.filename_hint || '',
    files: (raw.files || []).map(f => ({
      id: f.id,
      filename: f.filename,
      size: f.size,
      contenttype: f.contenttype,
    })),
    uploadingFile: null,
    uploadProgress: 0,
    uploadError: null,
    minSelections: raw.min_selections ?? 1,
    maxSelections: raw.max_selections ?? 1,
  }
}

function handleCodeGrader(raw) {
  return {
    graderFeedback: raw.grader_feedback || null,
    minRequired: raw.min_required || 1,
    maxRequired: raw.max_required || 1,
    filenameMask: raw.filename_mask || '',
    filenameHint: raw.filename_hint || '',
    files: (raw.files || []).map(f => ({
      id: f.id,
      filename: f.filename,
      size: f.size,
      contenttype: f.contenttype,
    })),
    uploadingFile: null,
    uploadProgress: 0,
    uploadError: null,
    minSelections: raw.min_selections ?? 1,
    maxSelections: raw.max_selections ?? 1,
  }
}

function handleNGNDropdown(raw) {
  return {
    prompt: raw.prompt || '',
    allocation: raw.allocation || null,
    fields: (raw.fields || []).map(f => ({
      guid: f.guid,
      options: f.options.map(o => ({ guid: o.guid, text: o.text })),
    })),
    minSelections: raw.min_selections ?? 1,
    maxSelections: raw.max_selections ?? 1,
  }
}

function handleDropdownTable(raw) {
  return {
    tableTitle: raw.table_title || '',
    allocation: raw.allocation || null,
    tableRows: (raw.rows || []).map(r => ({ idx: r.idx, text: r.text, guid: r.guid })),
    tableColumns: (raw.columns || []).map(c => ({ idx: c.idx, title: c.title, guid: c.guid })),
    cellOptions: raw.cell_options || {},
    minSelections: raw.min_selections ?? 1,
    maxSelections: raw.max_selections ?? 1,
  }
}

function handleHighlight(raw) {
  return {
    prompt: raw.prompt || '',
    allocation: raw.allocation || null,
    phrases: (raw.phrases || []).map(p => ({ guid: p.guid, text: p.text })),
    minSelections: raw.min_selections ?? 1,
    maxSelections: raw.max_selections ?? 1,
  }
}

function handleHotspot(raw) {
  return {
    imageUrl: raw.image_url || '',
    imageAlt: raw.image_alt || '',
    allocation: raw.allocation || null,
    // shape/coords/correct are present only once the answer may be shown
    regions: (raw.regions || []).map(r => ({
      guid: r.guid, label: r.label, correct: !!r.correct, shape: r.shape || null, coords: r.coords || [],
    })),
    minSelections: raw.min_selections ?? 1,
    maxSelections: raw.max_selections ?? 1,
  }
}

function handleDragDrop(raw) {
  return {
    prompt: raw.prompt || '',
    allocation: raw.allocation || null,
    dragdropBlocks: (raw.dragdrop_blocks || []).map(b => ({ guid: b.guid, title: b.title })),
    dragdropOptions: (raw.dragdrop_options || []).map(o => ({ guid: o.guid, text: o.text, blockGuid: o.block_guid })),
    dragdropFields: (raw.dragdrop_fields || []).map(f => ({ id: f.id, guid: f.guid, blockGuid: f.block_guid })),
    minSelections: raw.min_selections ?? 1,
    maxSelections: raw.max_selections ?? 1,
  }
}

function handleMatching(raw) {
  return {
    allocation: raw.allocation || null,
    items: (raw.items || []).map(item => ({ guid: item.guid, text: item.text })),
    options: (raw.options || []).map(option => ({ guid: option.guid, text: option.text })),
    minSelections: raw.min_selections ?? 1,
    maxSelections: raw.max_selections ?? 1,
  }
}

function handleBowtie(raw) {
  const mapSection = (section) => ({
    title: section.title,
    numCorrect: section.num_correct,
    options: section.options.map(o => ({ guid: o.guid, text: o.text })),
  })

  return {
    allocation: raw.allocation || null,
    bowtie: raw.bowtie ? {
      left: mapSection(raw.bowtie.left),
      center: mapSection(raw.bowtie.center),
      right: mapSection(raw.bowtie.right),
    } : null,
    minSelections: raw.min_selections ?? 1,
    maxSelections: raw.max_selections ?? 1,
  }
}

function handleMatrix(raw) {
  return {
    matrixTitle: raw.matrix_title || '',
    inputType: raw.input_type || null,
    allocation: raw.allocation || null,
    maxanswers: raw.maxanswers || 1,
    tableRows: (raw.rows || []).map(r => ({ guid: r.guid, text: r.text })),
    tableColumns: (raw.columns || []).map(c => ({ guid: c.guid, title: c.title })),
    minSelections: raw.min_selections ?? 1,
    maxSelections: raw.max_selections ?? 1,
  }
}

function handleGrouping(raw) {
  return {
    allocation: raw.allocation || null,
    maxanswers: raw.maxanswers || 1,
    tableRows: (raw.rows || []).map(r => ({ idx: r.idx, text: r.text, guid: r.guid })),
    tableColumns: (raw.columns || []).map(c => ({ idx: c.idx, title: c.title, guid: c.guid })),
    cellOptions: raw.cell_options || {},
    minSelections: raw.min_selections ?? 1,
    maxSelections: raw.max_selections ?? 1,
  }
}

const handlers = {
  'caseset': () => ({}),
  'multiplechoice': handleMultipleChoice,
  'truefalse': handleTrueFalse,
  'shortanswer': handleShortAnswer,
  'essay': handleEssay,
  'coder': handleCoder,
  'fileupload': handleFileUpload,
  'codegrader': handleCodeGrader,
  'ngn_dropdown': handleNGNDropdown,
  'ngn-dropdown-table': handleDropdownTable,
  'ngn_highlight': handleHighlight,
  'ngn-dragdrop': handleDragDrop,
  'matching': handleMatching,
  'ngn-bowtie': handleBowtie,
  'ngn-matrix': handleMatrix,
  'ngn-grouping': handleGrouping,
  'hotspot': handleHotspot,
}

export function transformQuestionData(type, rawData) {
  const handler = handlers[type]
  return handler ? handler(rawData || {}) : (rawData || {})
}
